import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import api from "../services/api";
import {
  FaArrowRight,
  FaArrowLeft,
  FaUsers,
  FaComments,
  FaUserPlus,
  FaCheck,
  FaHeart,
  FaComment,
  FaPaperPlane,
  FaSpinner,
  FaSmile,
  FaLightbulb,
  FaTimes,
} from "react-icons/fa";
import { getProfilePicture } from "../utils/avatar";
import { getPostSuggestionConfig } from "../data/post_suggestions";
import CommunityMap from "./CommunityMap";

// Auto-discover topic files for stance labels
const topicContext = require.context("../data/survey_topics", false, /^\.\/topic_.*\.json$/);
const TOPIC_STANCE_LABELS = {};
topicContext.keys().forEach((filename) => {
  const topic = topicContext(filename);
  if (topic.topicId && topic.stanceLabels) {
    TOPIC_STANCE_LABELS[topic.topicId] = topic.stanceLabels;
  }
});

// Progress indicator component
const ProgressSteps = ({ currentStep, totalSteps }) => {
  return (
    <div className="flex items-center justify-center mb-8">
      {Array.from({ length: totalSteps }, (_, i) => (
        <React.Fragment key={i}>
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
              i + 1 < currentStep
                ? "bg-spiders-primary-500 text-white"
                : i + 1 === currentStep
                ? "bg-spiders-primary-500 text-white ring-4 ring-spiders-primary-500/30"
                : "bg-spiders-bg-elevated text-spiders-text-muted"
            }`}
          >
            {i + 1 < currentStep ? <FaCheck className="w-4 h-4" /> : i + 1}
          </div>
          {i < totalSteps - 1 && (
            <div
              className={`w-12 h-1 mx-2 rounded transition-all duration-300 ${
                i + 1 < currentStep ? "bg-spiders-primary-500" : "bg-spiders-bg-elevated"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Step 1: Follow Members
const FollowMembersStep = ({ users, followedUsers, toggleFollow, onNext, onBack, loading, stanceLabels }) => {
  const canProceed = followedUsers.length >= 2;
  const [expandedUser, setExpandedUser] = useState(null);

  // Get stance label and color (topic-specific labels with fallback)
  const proLabel = stanceLabels?.pro || "Supports this";
  const contraLabel = stanceLabels?.contra || "Opposes this";

  const getStanceInfo = (user) => {
    const stance = user.agentConfig?.stance;
    const position = user.ideologicalPosition;

    if (stance === "pro" || position > 0.2) {
      return { label: proLabel, color: "bg-spiders-accent-500/20 text-spiders-accent-400", icon: "👍" };
    } else if (stance === "contra" || position < -0.2) {
      return { label: contraLabel, color: "bg-spiders-primary-500/20 text-spiders-primary-400", icon: "👎" };
    }
    return { label: "Moderate", color: "bg-gray-500/20 text-gray-400", icon: "⚖️" };
  };

  // Truncate text helper
  const truncate = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + "...";
  };

  return (
    <div className="animate-fadeIn">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-spiders-accent-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaUsers className="w-8 h-8 text-spiders-accent-400" />
        </div>
        <h1 className="text-2xl font-bold text-spiders-text-primary mb-2">
          Connect with the Community
        </h1>
        <p className="text-spiders-text-secondary">
          Follow at least <span className="text-spiders-primary-400 font-semibold">2 members</span> to see their posts in your feed
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <FaSpinner className="animate-spin w-8 h-8 text-spiders-primary-400" />
        </div>
      ) : (
        <div className="space-y-3 mb-6 max-h-[400px] overflow-y-auto pr-2">
          {users.map((user) => {
            const isFollowed = followedUsers.includes(user._id);
            const isExpanded = expandedUser === user._id;
            const stanceInfo = getStanceInfo(user);

            return (
              <div
                key={user._id}
                className={`spiders-card overflow-hidden transition-all duration-200 ${
                  isFollowed
                    ? "ring-2 ring-spiders-primary-500 bg-spiders-primary-500/5"
                    : "hover:bg-spiders-bg-elevated/50"
                }`}
              >
                {/* Main card content */}
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <img
                      src={getProfilePicture(user.username, user.avatarSeed)}
                      alt={user.username}
                      className={`w-12 h-12 rounded-full flex-shrink-0 transition-all ${
                        isFollowed ? "ring-2 ring-spiders-primary-500" : ""
                      }`}
                    />

                    {/* User info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-spiders-text-primary">
                          @{user.username}
                        </h3>
                        {/* Stance badge */}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${stanceInfo.color}`}>
                          {stanceInfo.icon} {stanceInfo.label}
                        </span>
                      </div>

                      {/* Bio preview */}
                      {user.bio && (
                        <p className="text-spiders-text-secondary text-sm mt-1 leading-relaxed">
                          {isExpanded ? user.bio : truncate(user.bio, 100)}
                        </p>
                      )}

                      {/* Sample message preview — styled like a mini post */}
                      {user.sampleMessage && (
                        <div className="mt-2 p-2.5 bg-spiders-bg-dark/40 rounded-lg border-l-2 border-spiders-primary-500/40">
                          <p className="text-spiders-text-secondary text-xs leading-relaxed">
                            "{isExpanded ? user.sampleMessage : truncate(user.sampleMessage, 120)}"
                          </p>
                        </div>
                      )}

                      {/* Expand/collapse button */}
                      {(user.bio?.length > 100 || user.sampleMessage?.length > 120) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedUser(isExpanded ? null : user._id);
                          }}
                          className="text-xs text-spiders-accent-400 hover:text-spiders-accent-300 mt-1"
                        >
                          {isExpanded ? "Show less" : "Show more"}
                        </button>
                      )}
                    </div>

                    {/* Follow button */}
                    <button
                      onClick={() => toggleFollow(user._id)}
                      className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                        isFollowed
                          ? "bg-spiders-primary-500 text-white"
                          : "bg-spiders-bg-elevated text-spiders-text-secondary hover:bg-spiders-bg-light border border-spiders-border"
                      }`}
                    >
                      {isFollowed ? (
                        <>
                          <FaCheck className="w-3 h-3" /> Following
                        </>
                      ) : (
                        <>
                          <FaUserPlus className="w-3 h-3" /> Follow
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Selection counter */}
      <div className="text-center mb-6">
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
          canProceed
            ? "bg-spiders-primary-500/20 text-spiders-primary-400"
            : "bg-spiders-bg-elevated text-spiders-text-muted"
        }`}>
          <span className="font-medium">
            {followedUsers.length} of 2 minimum selected
          </span>
          {canProceed && <FaCheck className="w-4 h-4" />}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-3 bg-spiders-bg-elevated hover:bg-spiders-bg-light text-spiders-text-secondary rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <FaArrowLeft /> Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className={`flex-1 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
            canProceed
              ? "spiders-btn-primary"
              : "bg-spiders-bg-elevated text-spiders-text-muted cursor-not-allowed"
          }`}
        >
          Continue <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

// Step 3: Conversation Digest
const ConversationDigestStep = ({ digestExchanges, onNext, onBack, loading }) => {
  return (
    <div className="animate-fadeIn">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-spiders-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaComments className="w-8 h-8 text-spiders-primary-400" />
        </div>
        <h1 className="text-2xl font-bold text-spiders-text-primary mb-2">
          The Conversation So Far
        </h1>
        <p className="text-spiders-text-secondary">
          Here's what people are talking about
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <FaSpinner className="animate-spin w-8 h-8 text-spiders-primary-400" />
        </div>
      ) : (
        <div className="space-y-4 mb-6">
          {digestExchanges.length === 0 ? (
            <div className="text-center py-8 text-spiders-text-muted">
              <FaComments className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>The conversation is just getting started. You'll be among the first to join!</p>
            </div>
          ) : (
            digestExchanges.map((exchange, idx) => (
              <div key={idx} className="spiders-card overflow-hidden">
                {/* Post — full prominence */}
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <img
                      src={getProfilePicture(exchange.post.author?.username || "unknown", exchange.post.author?.avatarSeed)}
                      alt=""
                      className="w-10 h-10 rounded-full flex-shrink-0 border border-spiders-border"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-spiders-text-primary text-sm">
                          @{exchange.post.author?.username || "unknown"}
                        </span>
                        <span className="text-spiders-text-muted text-xs">
                          {new Date(exchange.post.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-spiders-text-primary text-sm leading-relaxed">
                        {exchange.post.content}
                      </p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="flex items-center gap-1 text-xs text-spiders-text-muted">
                          <FaHeart className="w-3 h-3" />
                          {exchange.post.likes?.length || 0}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-spiders-text-muted">
                          <FaComment className="w-3 h-3" />
                          {exchange.post.comments?.length || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Replies — indented, smaller, visually subordinate */}
                {exchange.comments && exchange.comments.length > 0 && (
                  <div className="border-t border-spiders-border">
                    {exchange.comments.map((comment, cIdx) => (
                      <div key={cIdx} className="ml-8 mr-4 py-3 flex items-start gap-2.5 border-l-2 border-spiders-primary-500/30 pl-4">
                        <img
                          src={getProfilePicture(comment.author?.username || "unknown", comment.author?.avatarSeed)}
                          alt=""
                          className="w-7 h-7 rounded-full flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="font-semibold text-spiders-text-secondary text-xs">
                            @{comment.author?.username || "unknown"}
                          </span>
                          <p className="text-spiders-text-secondary text-xs leading-relaxed mt-0.5">
                            {comment.content}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      <p className="text-spiders-text-muted text-xs text-center mb-4">
        This is a sample of the ongoing discussion. There's much more to explore
      </p>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-3 bg-spiders-bg-elevated hover:bg-spiders-bg-light text-spiders-text-secondary rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <FaArrowLeft /> Back
        </button>
        <button
          onClick={onNext}
          className="flex-1 spiders-btn-primary py-3 flex items-center justify-center gap-2"
        >
          Continue <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

// Step 4: First Post
const FirstPostStep = ({ condition, conditionConfig, postContent, setPostContent, onSubmit, onBack, onSkip, submitting }) => {
  const topic = condition?.topic || {};
  const communicationStyle = conditionConfig?.agents?.communicationStyle;
  const isEmotional = communicationStyle === "emotional";
  const minLength = 20;
  const maxLength = 280;
  const canSubmit = postContent.trim().length >= minLength;

  const customPrompt = conditionConfig?.onboarding?.firstPostPrompt;
  const placeholder = customPrompt || "What stood out to you from the conversation?";

  const hashtags = topic.hashtags || [];

  // Emoji picker state
  const [showEmojis, setShowEmojis] = useState(false);

  // Suggestion panel state (3-stage: stance → argument → tone)
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionStage, setSuggestionStage] = useState(1);
  const [suggestionStance, setSuggestionStance] = useState(null);
  const [suggestionArgument, setSuggestionArgument] = useState(null);

  const suggestionConfig = useMemo(
    () => getPostSuggestionConfig(topic?.name),
    [topic?.name]
  );

  const handleOpenSuggestions = () => {
    setShowSuggestions(true);
    setSuggestionStage(1);
    setSuggestionStance(null);
    setSuggestionArgument(null);
  };

  const handleSelectStance = (stance) => {
    setSuggestionStance(stance);
    setSuggestionStage(2);
  };

  const handleSelectArgument = (argId) => {
    setSuggestionArgument(argId);
    setSuggestionStage(3);
  };

  const handleSelectTone = (toneId) => {
    const text = suggestionConfig?.suggestions?.[suggestionStance]?.[suggestionArgument]?.[toneId];
    if (text) {
      setPostContent(text);
    }
    setShowSuggestions(false);
    setSuggestionStage(1);
    setSuggestionStance(null);
    setSuggestionArgument(null);
  };

  const handleSuggestionBack = () => {
    if (suggestionStage === 3) {
      setSuggestionStage(2);
      setSuggestionArgument(null);
    } else if (suggestionStage === 2) {
      setSuggestionStage(1);
      setSuggestionStance(null);
    }
  };

  const handleDismissSuggestions = () => {
    setShowSuggestions(false);
    setSuggestionStage(1);
    setSuggestionStance(null);
    setSuggestionArgument(null);
  };

  return (
    <div className="animate-fadeIn">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-spiders-accent-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaPaperPlane className="w-8 h-8 text-spiders-accent-400" />
        </div>
        <h1 className="text-2xl font-bold text-spiders-text-primary mb-2">
          {isEmotional ? "Jump into the Conversation" : "Share Your Perspective"}
        </h1>
        <p className="text-spiders-text-secondary">
          {isEmotional
            ? "You've seen what others are saying. Now it's your turn."
            : "Based on what you've read, share your thoughts with the community"}
        </p>
      </div>

      <div className="spiders-card p-6 mb-6">
        <label className="block text-spiders-text-primary font-medium mb-3">
          {topic.name
            ? `What's your take on ${topic.name}?`
            : "What would you like to share with the community?"}
        </label>

        <textarea
          value={postContent}
          onChange={(e) => setPostContent(e.target.value.slice(0, maxLength))}
          placeholder={placeholder}
          rows={4}
          className="w-full p-4 bg-spiders-bg-elevated text-spiders-text-primary text-[15px] leading-relaxed rounded-xl border border-spiders-border focus:border-spiders-primary-500 focus:outline-none focus:ring-2 focus:ring-spiders-primary-500/20 resize-y min-h-[100px] max-h-[300px] transition-all"
        />

        {/* Action bar — emoji + inspiration + char count */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-spiders-border">
          <div className="flex items-center gap-1">
            {/* Emoji toggle */}
            <button
              type="button"
              onClick={() => setShowEmojis(prev => !prev)}
              className={`p-2.5 rounded-full transition-all duration-200 ${
                showEmojis
                  ? 'text-spiders-primary-400 bg-spiders-primary-500/20'
                  : 'text-spiders-accent-400 hover:bg-spiders-accent-500/20 hover:text-spiders-accent-300 hover:scale-110'
              }`}
              title="Add emoji"
            >
              <FaSmile className="w-5 h-5" />
            </button>

            {/* Inspiration button */}
            {!showSuggestions && suggestionConfig && (
              <button
                type="button"
                onClick={handleOpenSuggestions}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-spiders-accent-400 hover:bg-spiders-accent-500/20 hover:text-spiders-accent-300 transition-all duration-200"
              >
                <FaLightbulb className="w-4 h-4" />
                <span className="text-xs font-medium">Need inspiration?</span>
              </button>
            )}
          </div>

          <span className={`text-sm ${
            postContent.length < minLength
              ? "text-spiders-text-muted"
              : postContent.length > maxLength * 0.9
              ? "text-spiders-warning"
              : "text-spiders-text-muted"
          }`}>
            {postContent.length}/{maxLength}
            {postContent.length < minLength && (
              <span className="ml-2">({minLength - postContent.length} more needed)</span>
            )}
          </span>
        </div>

        {/* Emoji picker (toggleable) */}
        {showEmojis && (
          <div className="flex flex-wrap gap-1.5 mt-2 p-2 bg-spiders-bg-elevated rounded-lg">
            {['😀','😂','🤣','😊','😍','🥰','😘','😎','🤔','😮','😢','😭','😤','🙄','😬','🤯','👍','👎','👏','🙌','👋','💪','❤️','💯','🔥','⚡','🌟','✨','💡','🎉','🚀','💎'].map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="text-lg hover:scale-125 transition-transform p-1"
                onClick={() => setPostContent((prev) => (prev + emoji).slice(0, maxLength))}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {/* 3-stage suggestion panel */}
        {showSuggestions && suggestionConfig && (
          <div className="mt-3 border border-spiders-border rounded-xl bg-spiders-bg-surface overflow-hidden shadow-lg">
            {/* Header with progress + back/close */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-spiders-bg-elevated/50 border-b border-spiders-border">
              <div className="flex items-center gap-3">
                {suggestionStage > 1 && (
                  <button type="button" onClick={handleSuggestionBack}
                    className="text-spiders-text-muted hover:text-spiders-text-primary transition-colors p-0.5">
                    <FaArrowLeft className="w-3 h-3" />
                  </button>
                )}
                <div className="flex items-center gap-2">
                  <FaLightbulb className="w-3.5 h-3.5 text-spiders-accent-400" />
                  <span className="text-xs font-medium text-spiders-text-primary">
                    {suggestionStage === 1 && "What's your position?"}
                    {suggestionStage === 2 && "What's your main argument?"}
                    {suggestionStage === 3 && "How do you want to say it?"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex gap-1">
                  {[1, 2, 3].map((s) => (
                    <div key={s} className={`w-1.5 h-1.5 rounded-full transition-all ${
                      s <= suggestionStage ? "bg-spiders-accent-400" : "bg-spiders-bg-elevated"
                    }`} />
                  ))}
                </div>
                <button type="button" onClick={handleDismissSuggestions}
                  className="text-spiders-text-muted hover:text-spiders-text-secondary p-0.5">
                  <FaTimes className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Stage 1: Stance */}
            {suggestionStage === 1 && (
              <div className="p-3 space-y-2">
                {Object.entries(suggestionConfig.stances).map(([stanceKey, stanceData]) => (
                  <button key={stanceKey} type="button"
                    onClick={() => handleSelectStance(stanceKey)}
                    className="w-full text-left px-4 py-3 rounded-lg border border-spiders-border hover:border-spiders-primary-500/50 hover:bg-spiders-primary-500/5 transition-all group">
                    <span className="text-sm font-medium text-spiders-text-primary group-hover:text-spiders-primary-300 transition-colors">
                      {stanceData.label}
                    </span>
                    <p className="text-xs text-spiders-text-muted mt-0.5">{stanceData.description}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Stage 2: Argument */}
            {suggestionStage === 2 && suggestionStance && (
              <div className="p-3 grid grid-cols-2 gap-2">
                {suggestionConfig.stances[suggestionStance]?.arguments.map((arg) => (
                  <button key={arg.id} type="button"
                    onClick={() => handleSelectArgument(arg.id)}
                    className="text-left px-3 py-3 rounded-lg border border-spiders-border hover:border-spiders-accent-400/50 hover:bg-spiders-accent-400/5 transition-all group">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-base">{arg.icon}</span>
                      <span className="text-xs font-medium text-spiders-text-primary group-hover:text-spiders-accent-300 transition-colors">
                        {arg.label}
                      </span>
                    </div>
                    <p className="text-[11px] text-spiders-text-muted leading-snug">{arg.description}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Stage 3: Tone — shows preview of the generated post */}
            {suggestionStage === 3 && suggestionStance && suggestionArgument && (
              <div className="p-3 space-y-2">
                {suggestionConfig.tones.map((tone) => {
                  const previewText = suggestionConfig.suggestions?.[suggestionStance]?.[suggestionArgument]?.[tone.id];
                  if (!previewText) return null;
                  return (
                    <button key={tone.id} type="button"
                      onClick={() => handleSelectTone(tone.id)}
                      className="w-full text-left px-4 py-3 rounded-lg border border-spiders-border hover:border-spiders-primary-500/40 hover:bg-spiders-primary-500/5 transition-all group">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-sm">{tone.icon}</span>
                        <span className="text-xs font-semibold text-spiders-text-primary">{tone.label}</span>
                        <span className="text-[10px] text-spiders-text-muted">{tone.description}</span>
                      </div>
                      <p className="text-xs text-spiders-text-secondary leading-relaxed italic">
                        "{previewText}"
                      </p>
                      <p className="text-[10px] text-spiders-accent-400 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        Tap to use — you can edit before posting
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Suggested Hashtags */}
      {hashtags.length > 0 && (
        <div className="mb-6">
          <p className="text-spiders-text-muted text-sm mb-2">Suggested hashtags:</p>
          <div className="flex flex-wrap gap-2">
            {hashtags.slice(0, 5).map((tag, index) => (
              <button
                key={index}
                onClick={() => {
                  const hashtag = ` #${tag}`;
                  if (!postContent.includes(hashtag.trim())) {
                    setPostContent((prev) => (prev + hashtag).slice(0, maxLength));
                  }
                }}
                className="text-xs px-3 py-1 bg-spiders-bg-elevated hover:bg-spiders-bg-light text-spiders-accent-400 rounded-full transition-colors"
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="py-3 px-6 bg-spiders-bg-elevated hover:bg-spiders-bg-light text-spiders-text-secondary rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <FaArrowLeft /> Back
        </button>
        <button
          onClick={onSkip}
          className="py-3 px-6 text-spiders-text-muted hover:text-spiders-text-secondary transition-colors text-sm"
          disabled={submitting}
        >
          Skip for now
        </button>
        <button
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          className={`flex-1 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
            canSubmit && !submitting
              ? "spiders-btn-primary"
              : "bg-spiders-bg-elevated text-spiders-text-muted cursor-not-allowed"
          }`}
        >
          {submitting ? (
            <>
              <FaSpinner className="animate-spin w-4 h-4" /> Posting...
            </>
          ) : (
            <>
              Post & Enter Discussion <FaArrowRight />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Main Onboarding Flow Component — 3-step flow
const TOTAL_STEPS = 2;

const OnboardingFlow = ({ condition, conditionConfig, onComplete }) => {
  const { user } = useContext(AuthContext);
  const { conditionId: surveyConditionId, instanceId: surveyInstanceId } = useContext(SurveyContext);

  // Use SurveyContext values, with AuthContext's user data as fallback
  const conditionId = surveyConditionId || user?.condition;
  const instanceId = surveyInstanceId || user?.instanceId;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Step data
  const [followedUsers, setFollowedUsers] = useState([]);
  const [postContent, setPostContent] = useState("");

  // Fetched data
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [digestExchanges, setDigestExchanges] = useState([]);

  // Fetch suggested users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      if (!conditionId || !instanceId) return;

      setLoading(true);
      setFetchError(null);
      try {
        const usersResponse = await api.get("/users/suggestions", {
          params: { condition: conditionId, instanceId, limit: 6 }
        });
        setSuggestedUsers(usersResponse.data || []);
      } catch (error) {
        console.error("Error fetching onboarding data:", error);
        setFetchError("Failed to load community data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [conditionId, instanceId]);

  // Fetch digest when moving to step 3 (after follow step)
  const fetchDigest = async () => {
    if (!instanceId || followedUsers.length === 0) return;

    setDigestLoading(true);
    setFetchError(null);
    try {
      const maxExchanges = conditionConfig?.onboarding?.digestConfig?.maxExchanges || 3;
      const response = await api.get("/messages/digest", {
        params: {
          instanceId,
          followedUserIds: followedUsers.join(","),
          limit: maxExchanges,
        }
      });
      setDigestExchanges(response.data || []);
    } catch (error) {
      console.error("Error fetching digest:", error);
      setFetchError("Failed to load conversation preview. Please try again.");
      setDigestExchanges([]);
    } finally {
      setDigestLoading(false);
    }
  };

  const toggleFollow = (userId) => {
    setFollowedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAdvanceToDigest = () => {
    fetchDigest();
    setStep(2);
  };

  const handleComplete = async (skipPost = false) => {
    setSubmitting(true);

    console.log("[OnboardingFlow] handleComplete called with:", {
      conditionId,
      instanceId,
      userId: user?.userId,
      followedUsers,
      postContentLength: postContent.trim().length,
      skipPost,
    });

    if (!conditionId || !instanceId) {
      console.error("[OnboardingFlow] Missing conditionId or instanceId!", { conditionId, instanceId });
    }

    try {
      // Follows are already executed in real-time by CommunityMap component
      console.log("[OnboardingFlow] Already following", followedUsers.length, "users (handled by CommunityMap)");

      // Post initial message if provided
      if (!skipPost && postContent.trim()) {
        console.log("[OnboardingFlow] Posting initial message");
        try {
          await api.post("/messages", {
            content: postContent.trim(),
            condition: conditionId,
            instanceId,
          });
          console.log("[OnboardingFlow] Message posted successfully");
        } catch (e) {
          console.error("[OnboardingFlow] Error posting message:", e.response?.data || e.message);
        }
      }

      // Mark onboarding as complete
      onComplete({
        followedUsers,
        postedContent: !skipPost && postContent.trim() ? postContent : null,
      });
    } catch (error) {
      console.error("Error completing onboarding:", error);
      // Still proceed even if some actions fail
      onComplete({
        followedUsers,
        postedContent: null,
        error: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-spiders-bg flex items-center justify-center p-4">
      <div className={`w-full ${step === 1 ? "max-w-4xl" : "max-w-2xl"} transition-all duration-300`}>
        <ProgressSteps currentStep={step} totalSteps={TOTAL_STEPS} />

        <div className="spiders-card p-6 md:p-8">
          {fetchError && (
            <div className="mb-4 p-4 bg-red-900/30 border border-red-500/30 rounded-lg flex items-center justify-between">
              <span className="text-red-300 text-sm">{fetchError}</span>
              <button
                onClick={() => { setFetchError(null); window.location.reload(); }}
                className="ml-4 px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium"
              >
                Retry
              </button>
            </div>
          )}
          {step === 1 && (
            <div className="animate-fadeIn">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-spiders-accent-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaUsers className="w-8 h-8 text-spiders-accent-400" />
                </div>
                <h1 className="text-2xl font-bold text-spiders-text-primary mb-2">
                  Meet the Community
                </h1>
                <p className="text-spiders-text-secondary">
                  Click on members to learn more about them. Follow at least <span className="text-spiders-primary-400 font-semibold">2 members</span> to continue.
                </p>
              </div>

              <CommunityMap
                followedUsers={followedUsers}
                onFollowChange={setFollowedUsers}
                stanceLabels={TOPIC_STANCE_LABELS[conditionConfig?.topic?.name] || conditionConfig?.topic?.stanceLabels}
                proLabel={TOPIC_STANCE_LABELS[conditionConfig?.topic?.name]?.pro || "Support"}
                contraLabel={TOPIC_STANCE_LABELS[conditionConfig?.topic?.name]?.contra || "Oppose"}
              />

              {/* Selection counter */}
              <div className="text-center my-4">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                  followedUsers.length >= 2
                    ? "bg-spiders-primary-500/20 text-spiders-primary-400"
                    : "bg-spiders-bg-elevated text-spiders-text-muted"
                }`}>
                  <span className="font-medium">
                    {followedUsers.length} of 2 minimum selected
                  </span>
                  {followedUsers.length >= 2 && <FaCheck className="w-4 h-4" />}
                </div>
              </div>

              <button
                onClick={() => setStep(2)}
                disabled={followedUsers.length < 2}
                className={`w-full py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                  followedUsers.length >= 2
                    ? "spiders-btn-primary"
                    : "bg-spiders-bg-elevated text-spiders-text-muted cursor-not-allowed"
                }`}
              >
                Continue <FaArrowRight />
              </button>
            </div>
          )}

          {step === 2 && (
            <FirstPostStep
              condition={condition}
              conditionConfig={conditionConfig}
              postContent={postContent}
              setPostContent={setPostContent}
              onSubmit={() => handleComplete(false)}
              onBack={() => setStep(1)}
              onSkip={() => handleComplete(true)}
              submitting={submitting}
            />
          )}
        </div>

        {/* SPIDERS branding */}
        <div className="text-center mt-6">
          <span className="text-spiders-text-muted text-sm">
            Powered by <span className="text-gradient-logo font-semibold">SPIDERS</span> Research Platform
          </span>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
